import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  VStack,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Link,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
} from "@chakra-ui/react";
import { PieChartIcon, GlobeIcon, ChevronDownIcon, DnaIcon } from "lucide-react";
import MultiSelectDropdown from "components/MultiSelectDropdown";
import SingleSelectDropdown from "components/SingleSelectDropdown";
import { uniqueStudiesQuery, mutationDataQuery } from "api/api-service";
import {
  countries,
  filterOptions,
  mapperForGeneDiseaseAbbr,
  parseGeneRouteParam,
} from "utils/utils";
import CollapsibleMutations from "components/CollapsibleMutations";
import CustomSpinner from "components/CustomSpinner";
import Charts from "../Charts";
import WorldMap from "../WorldMap";
import GeneticFindings from "../GeneticFindings";
import AdvancedPagination from "components/AdvancedPagination";
import Search from "components/Search";
import MutationDataDisplay from "components/MutationDataDisplay";

// List of genes that should show the additional button
const GENES_WITH_EXTRA_INFO = ['THAP1', 'GBA1', 'LRRK2', 'PINK1'];

const clinicalSummaryGroupLabels = {
  GP2: "GP2",
  MDSGENE: "MDSGene",
};

const getClinicalSummaryTitle = (geneName) => {
  const { datasetPrefix, baseDisease, gene } = parseGeneRouteParam(geneName);
  const groupLabel =
    clinicalSummaryGroupLabels[datasetPrefix] || "Joint GP2 + MDSGene";
  const diseaseGeneLabel =
    baseDisease && gene
      ? mapperForGeneDiseaseAbbr(`${baseDisease}-${gene}`)
      : mapperForGeneDiseaseAbbr(geneName);

  return `Clinical Data Summaries (${groupLabel}) for ${diseaseGeneLabel}`;
};

const isPubMedId = (value) => /^\d+$/.test(String(value ?? "").trim());

const formatStudyDesign = (study) => {
  if (!isPubMedId(study?.pmid)) {
    return "";
  }

  const value = String(study?.study_design ?? "").trim();
  if (!value || ["-99", "unknown", "n/a", "n.a."].includes(value.toLowerCase())) {
    return "";
  }

  return value;
};

const Gene = () => {
  const { geneName } = useParams();
  const { datasetPrefix, disease, gene } = parseGeneRouteParam(geneName);
  const showStudyDesignColumn = datasetPrefix !== "GP2";
  const showExtraButton = useMemo(() => {
    return GENES_WITH_EXTRA_INFO.includes(gene);
  }, [gene]);
  const {
    isOpen: isChartsOpen,
    onOpen: onChartsOpen,
    onClose: onChartsClose,
  } = useDisclosure();
  const {
    isOpen: isWorldMapOpen,
    onOpen: onWorldMapOpen,
    onClose: onWorldMapClose,
  } = useDisclosure();
  const {
    isOpen: isGeneticFindingsOpen,
    onOpen: onGeneticFindingsOpen,
    onClose: onGeneticFindingsClose,
  } = useDisclosure();

  const [filters, setFilters] = useState({
    filterCriteria: 0,
    aao: 50,
    countries: [],
    mutations: [],
  });

  const [selectedPatientFilter, setSelectedPatientFilter] = useState({
    type: "all included patients",
    age: 50,
  });
  const [selectedMutations, setSelectedMutations] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedMutationData, setSelectedMutationData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialMutationOptions, setInitialMutationOptions] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAll, setShowAll] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    ...uniqueStudiesQuery(disease, gene, filters),
    keepPreviousData: true,
  });

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((study) => {
      const searchTermLower = searchTerm.toLowerCase();
      return (
        study?.author_year?.toLowerCase().includes(searchTermLower) ||
        study?.pmid?.toString().includes(searchTermLower) ||
        (showStudyDesignColumn &&
          formatStudyDesign(study).toLowerCase().includes(searchTermLower)) ||
        study?.ethnicity?.toString().toLowerCase().includes(searchTermLower) ||
        study?.mutations?.some((mutationGroup) => {
          if (mutationGroup.type === "single") {
            return mutationGroup.name.toLowerCase().includes(searchTermLower);
          } else if (mutationGroup.type === "compound_het") {
            return mutationGroup.mutations.some((mutation) =>
              mutation.name.toLowerCase().includes(searchTermLower)
            );
          }
          return false;
        })
      );
    });
  }, [data, searchTerm, showStudyDesignColumn]);

  const paginatedData = useMemo(() => {
    if (showAll) return filteredData;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage, showAll]);

  const mutationOptions = useMemo(() => {
    if (initialMutationOptions) return initialMutationOptions;

    if (!data) return [];

    // Extract unique pathogenicity values from mutations
    const pathogenicityValues = new Set();
    data.forEach((study) => {
      study.mutations.forEach((mutation) => {
        if (mutation.pathogenicity) {
          pathogenicityValues.add(mutation.pathogenicity);
        }
      });
    });

    // Combine pathogenicity values with mutations
    const newOptions = [
      ...Array.from(pathogenicityValues), // Include unique pathogenicity values
      ...(data.flatMap((study) => study.mutations) || []),
    ];

    setInitialMutationOptions(newOptions);
    return newOptions;
  }, [data, initialMutationOptions]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleMutationClick = async (
    mutationName,
    genotype,
    pmid,
    mutation
  ) => {
    setIsModalOpen(true);
    const maxRetries = 3;
    const retryDelay = 500;

    const fetchWithRetry = async (attempt = 0) => {
    try {
      const result = await queryClient.fetchQuery({
        ...mutationDataQuery(disease, gene, pmid, mutationName),
      });

      setSelectedMutationData({
        data: result,
        originalMutation: mutationName,
        fullMutationData: mutation,
      });
    } catch (error) {
        console.error(`Attempt ${attempt + 1} failed:`, error);
        if (attempt < maxRetries) {
          setTimeout(() => fetchWithRetry(attempt + 1), retryDelay);
        } else {
          setSelectedMutationData({ error: "Failed to fetch mutation data after 3 attempts" });
    }
      }
    };

    fetchWithRetry();
  };

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      filterCriteria: filterOptions.indexOf(selectedPatientFilter.type),
      aao: selectedPatientFilter.type.includes("AAO")
        ? selectedPatientFilter.age
        : prevFilters.aao,
    }));
  }, [selectedPatientFilter]);

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      mutations: selectedMutations,
    }));
  }, [selectedMutations]);

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      countries: selectedCountries.map((country) =>
        Object.keys(countries).find((key) => countries[key] === country)
      ),
    }));
  }, [selectedCountries]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const getFilterParams = () => ({
    disease_abbrev: disease,
    gene,
    directory: "excel",
    filterCriteria: filters.filterCriteria,
    aao: filters.aao,
    countries: filters.countries.join(","),
    mutations: filters.mutations.join(","),
  });

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  if (error) return <Text>An error occurred: {error.message}</Text>;

  return (
    <Box maxW="1200px" mx="auto" p={5}>
      <VStack spacing={8} align="stretch">
        <Flex justifyContent="space-between" alignItems="center">
          <Heading as="h1" size="xl" color="brand.700">
            Overview of included studies for <br />
            {mapperForGeneDiseaseAbbr(geneName)}
          </Heading>
          <Flex gap={4} direction="column">
            <Flex gap={4} >
            <Button
              leftIcon={<PieChartIcon />}
              onClick={onChartsOpen}
              variant="outline"
              colorScheme="brand"
            >
              View Clinical Data Summaries
            </Button>
            <Button
              leftIcon={<GlobeIcon />}
              onClick={onWorldMapOpen}
              variant="outline"
              colorScheme="orchid"
            >
              View World Map
            </Button>
            </Flex>
            {showExtraButton && (
                <Button
                    leftIcon={<DnaIcon />}
                    onClick={onGeneticFindingsOpen}
                    variant="outline"
                    colorScheme="neutral"
                >
                  View Genetic Findings
                </Button>
            )}
          </Flex>
        </Flex>

        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" wrap="wrap" gap={4}>
            <Box width="48%">
              <SingleSelectDropdown
                options={filterOptions}
                placeholder="Select patient filter"
                label="Filter for"
                onChange={setSelectedPatientFilter}
              />
            </Box>
            <Box width="48%">
              <MultiSelectDropdown
                options={mutationOptions}
                placeholder="Select variant"
                label="Carrying"
                selectedItems={selectedMutations}
                setSelectedItems={setSelectedMutations}
              />
            </Box>
            <Box width="48%">
              <MultiSelectDropdown
                options={Object.values(countries)}
                placeholder="Select country"
                label="Country"
                selectedItems={selectedCountries}
                setSelectedItems={setSelectedCountries}
              />
            </Box>
          </Flex>

          <Divider />

          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <Search onSearch={handleSearch} />
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                variant="outline"
                borderRadius="full"
              >
                {itemsPerPage} per page
              </MenuButton>
              <MenuList>
                {[5, 10, 20, 50, 100].map((value) => (
                  <MenuItem key={value} onClick={() => setItemsPerPage(value)}>
                    {value} per page
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>

          <Box overflowX="auto" boxShadow="md" borderRadius="lg">
            {isLoading ? (
              <Flex w="full" mt={5} justify="center">
                <CustomSpinner type="MG" color="#0c8dc3" size={200} />
              </Flex>
            ) : (
              <Table variant="simple" colorScheme="neutral">
                <Thead bg="neutral.50">
                  <Tr whiteSpace="pre">
                    <Th>Study</Th>
                    {showStudyDesignColumn && <Th>Study design</Th>}
                    <Th>N cases</Th>
                    <Th>Ethnicities</Th>
                    <Th>Sex (% ♂)</Th>
                    <Th>Mean AAO (+/- SD)</Th>
                    <Th>Reported mutations</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedData?.length > 0 ? (
                    paginatedData.map((study) => (
                      <Tr key={study.pmid}>
                        <Td whiteSpace="pre">
                          <Link
                              as={RouterLink}
                              color="brand.600"
                              to={`/genes/${geneName}/${encodeURIComponent(
                                String(study.pmid)
                              )}`}
                          >
                            {study?.author_year}
                          </Link>
                          {isPubMedId(study?.pmid) && (
                            <>
                              <br />
                              <sub>PMID:{study?.pmid}</sub>
                            </>
                          )}
                        </Td>
                        {showStudyDesignColumn && (
                          <Td textTransform="capitalize">{formatStudyDesign(study)}</Td>
                        )}
                        <Td>{study.number_of_cases}</Td>
                        <Td>
                        {study.ethnicity && study.ethnicity !== -99
                            ? study.ethnicity
                                .split(",")
                                .map((item) => item.trim())
                                .filter(Boolean)
                                .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
                                .join(", ")
                            : "N/A"}
                        </Td>
                        <Td>
                          {study.proportion_of_male_patients === -99
                            ? "N/A"
                            : (study.proportion_of_male_patients * 100).toFixed(
                                2
                              ) + "%"}
                        </Td>
                        <Td whiteSpace="pre">
                          {study.mean_age_at_onset !== -99
                            ? `${study.mean_age_at_onset} ${
                                study.std_dev_age_at_onset
                                  ? `\n(+/- ${study.std_dev_age_at_onset})`
                                  : ""
                              }`
                            : "N/A"}
                        </Td>
                        <Td>
                          <CollapsibleMutations
                            mutations={study.mutations}
                            onMutationClick={(mutP, originalMutation) =>
                              handleMutationClick(
                                mutP,
                                originalMutation,
                                study.pmid,
                                study.full_mutations
                              )
                            }
                          />
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td
                        colSpan={showStudyDesignColumn ? 7 : 6}
                        textAlign="center"
                        py={8}
                      >
                        <Text fontSize="lg" color="neutral.500">
                          No results found
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            )}
          </Box>

          <Flex justify="space-between" align="center">
            <Button
              onClick={() => setShowAll(!showAll)}
              variant={showAll ? "solid" : "outline"}
              colorScheme="brand"
              borderRadius="full"
            >
              {showAll ? "Show Paginated" : "Show All Results"}
            </Button>

            {!showAll && (
              <AdvancedPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </Flex>
        </VStack>

        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent maxW="1000px">
            <ModalHeader fontSize="xl" fontWeight="bold" color="brand.600" mb={4}>
              Mutation Data
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedMutationData ? (
                selectedMutationData.error ? (
                  <Text color="fuchsia.500">{selectedMutationData.error}</Text>
                ) : (
                  <MutationDataDisplay data={selectedMutationData.data} />
                )
              ) : (
                <CustomSpinner type="MG" color="#0c8dc3" size={200} />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
        <Modal isOpen={isChartsOpen} onClose={onChartsClose} size="full">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{getClinicalSummaryTitle(geneName)}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Charts geneName={geneName} filters={getFilterParams()} />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Modal isOpen={isWorldMapOpen} onClose={onWorldMapClose} size="full">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>World Map for {mapperForGeneDiseaseAbbr(geneName)}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <WorldMap geneName={geneName} filters={getFilterParams()} />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Modal isOpen={isGeneticFindingsOpen} onClose={onGeneticFindingsClose} size="full">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Genetic findings for {mapperForGeneDiseaseAbbr(geneName)}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <GeneticFindings geneName={geneName} filters={getFilterParams()} />
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default Gene;
